/**
 * FRONT-2550 — save a Files item's COMPLETE tag list (smart tagging's "remove a
 * tag"). One hook, two endpoints, because the two item kinds persist it
 * differently: an upload through `PUT /api/files/:id`, a document through
 * `PATCH /api/documents/:filename` — both take `{ tags }` (a removals-only
 * subset of the current list) and both write the
 * `okf_tags` column the Files filter reads (never the legacy CSV list).
 *
 * `mutateAsync` resolves once the save has landed; the refreshes are started
 * but NOT awaited, so a slow list refetch can never hold a finished removal
 * hostage. No toast here — `ItemTags` owns the success/failure wording so the
 * failure path can restore the chip in the same place it toasts.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { filesOverviewKeys } from '~/lib/query-keys';
import { documentKeys } from '~/hooks/queries/use-documents';
import { fileKeys } from '~/hooks/queries/use-files';
import { enrichmentActivityKeys } from '~/hooks/queries/use-enrichment-activity';
import { knowledgeSearchKeys } from '~/hooks/queries/use-knowledge-search';
import { catalogueKeys } from '~/hooks/queries/use-knowledge-catalogue';

export type TaggedItemSource = 'file' | 'document';

export function useSetItemTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ source, itemRef, tags }: { source: TaggedItemSource; itemRef: string; tags: string[] }) =>
      source === 'file'
        ? api.put<unknown>(`/api/files/${encodeURIComponent(itemRef)}`, { tags })
        : api.patch<unknown>(`/api/documents/${encodeURIComponent(itemRef)}`, { tags }),
    onSuccess: (_, { source, itemRef }) => {
      // The item's own view, the Files overview (its tag chips and the
      // "Tagged by EDDY" filter), the settings card's recent list (it shows
      // the item's CURRENT tags), and the knowledge search — its listings AND
      // its facet counts — plus the catalogue overview, all of which read tags.
      qc.invalidateQueries({
        queryKey: source === 'file' ? fileKeys.detail(itemRef) : documentKeys.detail(itemRef),
      });
      qc.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
      qc.invalidateQueries({ queryKey: enrichmentActivityKeys.all });
      qc.invalidateQueries({ queryKey: knowledgeSearchKeys.all });
      qc.invalidateQueries({ queryKey: catalogueKeys.all });
    },
  });
}
